import { ref, computed, watch, isRef } from 'vue'

/**
 * 表格批次選取 hook
 * 特點：
 * 1. 支援單頁或跨頁選取（可透過 clearOnPageChange 控制）
 * 2. 不依賴 Element UI 內建 selection
 * 3. API 簡潔直觀
 * 4. 自動監聽 tableData 變化，換頁時自動同步選取狀態
 *
 * @param {Object} options - Hook 選項
 * @param {Function} options.getKey - 從 row 取得唯一識別鍵的函式
 * @param {Function} options.getCurrentPageRows - 取得當前頁所有 rows 的函式
 * @param {import('vue').Ref|Function} [options.tableData] - 表格資料的 ref 或 getter，用於自動監聽換頁
 * @param {Function} [options.onChange] - 選取變更時的回調函式
 * @param {Boolean} [options.clearOnPageChange=true] - 換頁時是否清除其他頁的選取狀態
 */
export const useTableBatchSelection = (options) => {
  const { getKey, getCurrentPageRows, tableData, onChange, clearOnPageChange = true } = options

  // ========== 內部狀態 ==========

  // 使用 ref 儲存選取的 IDs 陣列（直接使用陣列以確保響應式）
  const selectedIds = ref([])

  // 使用 Map 儲存選取的 rows（key -> row）用於快速查詢
  const selectedRowsMap = ref(new Map())

  // ========== Computed States ==========

  /** 選取的 rows 陣列 */
  const selectedRows = computed(() => selectedIds.value.map(id => selectedRowsMap.value.get(id)).filter(Boolean))

  /** 選取的數量 */
  const selectedCount = computed(() => selectedIds.value.length)

  /** 是否全選（當前頁） */
  const isAllSelected = computed(() => {
    const currentRows = getCurrentPageRows()
    if (!currentRows || currentRows.length === 0) return false
    if (selectedIds.value.length === 0) return false

    return currentRows.every(row => {
      const key = getKey(row)
      return selectedIds.value.includes(key)
    })
  })

  /** 是否為不確定狀態（部分選取） */
  const isIndeterminate = computed(() => {
    const currentRows = getCurrentPageRows()
    if (!currentRows || currentRows.length === 0) return false
    if (selectedIds.value.length === 0) return false
    if (isAllSelected.value) return false

    return currentRows.some(row => {
      const key = getKey(row)
      return selectedIds.value.includes(key)
    })
  })

  // ========== Private Methods ==========

  /** 觸發 onChange 回調 */
  const triggerChange = () => {
    if (onChange) {
      onChange(selectedIds.value, selectedRows.value)
    }
  }

  // ========== Public Methods ==========

  /**
   * 檢查某個 row 是否被選取（響應式）
   * @param {Object} row - 要檢查的資料列
   * @returns {ComputedRef<Boolean>}
   */
  const isRowSelected = (row) => {
    const key = getKey(row)
    return computed(() => selectedIds.value.includes(key))
  }

  /**
   * 切換單行選取狀態
   * @param {Object} row - 要切換的資料列
   */
  const toggleRow = (row) => {
    const key = getKey(row)
    const index = selectedIds.value.indexOf(key)

    if (index > -1) {
      // 取消選取：移除 ID 和 row
      selectedIds.value = selectedIds.value.filter(id => id !== key)
      selectedRowsMap.value.delete(key)
    } else {
      // 選取：加入 ID 和 row
      selectedIds.value = [...selectedIds.value, key]
      selectedRowsMap.value.set(key, row)
    }

    triggerChange()
  }

  /**
   * 全選或全不選當前頁
   * @param {Boolean} checked - true 為全選，false 為全不選
   */
  const toggleAll = (checked) => {
    const currentRows = getCurrentPageRows()
    if (!currentRows) return

    if (checked) {
      // 全選：將當前頁所有項目加入
      const newIds = [...selectedIds.value]
      const newMap = new Map(selectedRowsMap.value)

      currentRows.forEach(row => {
        const key = getKey(row)
        if (!newIds.includes(key)) {
          newIds.push(key)
          newMap.set(key, row)
        }
      })

      selectedIds.value = newIds
      selectedRowsMap.value = newMap
    } else {
      // 全不選：移除當前頁所有項目
      const currentPageKeys = currentRows.map(row => getKey(row))
      selectedIds.value = selectedIds.value.filter(id => !currentPageKeys.includes(id))

      currentPageKeys.forEach(key => {
        selectedRowsMap.value.delete(key)
      })
    }

    triggerChange()
  }

  /**
   * 按條件篩選並選取當前頁的項目
   * @param {Function} filterFn - 篩選函式，回傳 true 表示選取該 row
   *
   * @example
   * // 選取所有狀態為 'pending' 的項目
   * selection.selectByFilter(row => row.replyStatus === 'pending')
   */
  const selectByFilter = (filterFn) => {
    const currentRows = getCurrentPageRows()
    if (!currentRows) return

    // 先清空當前頁的選取
    const currentPageKeys = currentRows.map(row => getKey(row))
    selectedIds.value = selectedIds.value.filter(id => !currentPageKeys.includes(id))

    currentPageKeys.forEach(key => {
      selectedRowsMap.value.delete(key)
    })

    // 依條件重新選取
    const filteredRows = currentRows.filter(filterFn)
    const newIds = [...selectedIds.value]
    const newMap = new Map(selectedRowsMap.value)

    filteredRows.forEach(row => {
      const key = getKey(row)
      newIds.push(key)
      newMap.set(key, row)
    })

    selectedIds.value = newIds
    selectedRowsMap.value = newMap

    triggerChange()
  }

  /**
   * 清空所有選取
   */
  const clear = () => {
    selectedIds.value = []
    selectedRowsMap.value.clear()
    triggerChange()
  }

  /**
   * 同步當前頁資料（換頁時呼叫）
   * 根據 clearOnPageChange 選項決定是否清除不在當前頁的選取項目
   */
  const syncCurrentPage = () => {
    // 如果設定為不清除其他頁選取，則跳過
    if (!clearOnPageChange) return

    const currentRows = getCurrentPageRows()
    if (!currentRows) return

    // 取得當前頁的所有 keys
    const currentPageKeys = new Set(currentRows.map(row => getKey(row)))

    // 清除不在當前頁的選取項目
    const keysToRemove = selectedIds.value.filter(id => !currentPageKeys.has(id))

    if (keysToRemove.length > 0) {
      selectedIds.value = selectedIds.value.filter(id => currentPageKeys.has(id))

      keysToRemove.forEach(key => {
        selectedRowsMap.value.delete(key)
      })

      triggerChange()
    }
  }

  // ========== Auto Watch ==========

  /**
   * 若有提供 tableData 參數，自動監聽其變化
   * 換頁時會自動執行 syncCurrentPage()
   */
  if (tableData) {
    // 支援 Ref 或 getter function
    const dataSource = isRef(tableData) ? () => tableData.value : tableData

    watch(
      dataSource,
      () => {
        syncCurrentPage()
      },
      { deep: true },
    )
  }

  // ========== Return ==========

  return {
    // States
    selectedIds,
    selectedRows,
    selectedCount,
    isAllSelected,
    isIndeterminate,

    // Methods
    isRowSelected,
    toggleRow,
    toggleAll,
    selectByFilter,
    clear,
    syncCurrentPage,
  }
}
